import PropTypes from 'prop-types'
import {components} from 'react-select'
import CreatableSelect from 'react-select/creatable'
import subDays from 'date-fns/subDays'
import startOfDay from 'date-fns/startOfDay'
import endOfDay from 'date-fns/endOfDay'

import {tagColors} from '../../colors.js'
import {hasClass} from '../../classNames.js'
import Icon from '../../icons/Icon.js'
import {visualizeInterval} from '../../../ordoro/ReportsPage/ReportForms/IntervalSummary.js'
import {
  dateTimeFormat,
  formatDateTime,
  getShortTimeZone,
  parseDate,
} from '../../date.js'
import {Count, plural} from '../Plural.js'
import {getState} from '../../../store.js'
import {REPORT_INTERVAL_ABBV_LOOKUP} from '../../constants/Reports.js'

export function parseInputValue(inputValue) {
  inputValue = inputValue || ''

  let [, filterType = '', filterOperator = '', filterValue] = inputValue.match(
    /^(?:(.*)?:(!=|[!=><-])?)?(.*)$/,
  )

  filterType = filterType.trim()
  filterValue = filterValue.trim()

  return [inputValue, filterType, filterOperator, filterValue]
}

export function summarizeDateFilter({
  dateType,
  startDate,
  endDate,
  intervalType,
  intervalAmount,
  intervalTrunc,
  timezone,
  intervalSummary,
}) {
  if (intervalType) {
    const [visualStartDateString, visualEndDateString] = visualizeInterval(
      intervalType,
      intervalAmount,
      intervalTrunc,
    )
    const startIntervalDate = new Date(visualStartDateString)
    const startIntervalStr = dateTimeFormat(startIntervalDate, formatDateTime)
    let startTimezoneStr = timezone
      ? ` ${getShortTimeZone(startIntervalDate, timezone)}`
      : ''

    const endIntervalDate = new Date(visualEndDateString)
    const endIntervalStr = dateTimeFormat(endIntervalDate, formatDateTime)
    const endTimezoneStr = timezone
      ? ` ${getShortTimeZone(endIntervalDate, timezone)}`
      : ''

    if (startTimezoneStr === endTimezoneStr) {
      startTimezoneStr = ''
    }

    const value = plural(intervalAmount)`interval of ${Count} ${intervalType}${[
      's',
    ]}${intervalTrunc ? ' truncated' : ''}`

    const label = intervalSummary
      ? `${value} (${startIntervalStr}${startTimezoneStr} to ${endIntervalStr}${endTimezoneStr})`
      : value

    return [
      {
        type: dateType,
        label,
        value,
        intervalType,
      },
    ]
  } else if (startDate || endDate) {
    let [startStr, startTimezoneStr] = startDate
      ? [
          dateTimeFormat(startDate),
          timezone ? ` ${getShortTimeZone(startDate, timezone)}` : '',
        ]
      : ['', '']
    const [endStr, endTimezoneStr] = endDate
      ? [
          dateTimeFormat(endDate),
          timezone ? ` ${getShortTimeZone(endDate, timezone)}` : '',
        ]
      : ['', '']

    if (startStr && endStr && startTimezoneStr === endTimezoneStr) {
      startTimezoneStr = ''
    }

    const label =
      startStr && endStr
        ? `${dateTimeFormat(startDate)}${startTimezoneStr} to ${dateTimeFormat(
            endDate,
          )}${endTimezoneStr}`
        : startStr
          ? `After ${startStr}${startTimezoneStr}`
          : `Before ${endStr}${endTimezoneStr}`

    const value =
      startStr && endStr
        ? `${dateTimeFormat(startDate)} to ${dateTimeFormat(endDate)}`
        : startStr
          ? `After ${startStr}`
          : `Before ${endStr}`

    return [
      {
        type: dateType,
        label,
        value,
      },
    ]
  }

  return []
}

export function processDateFilterValue(
  inputValue,
  filterType,
  filterOperator,
  filterValue,
  getDateTypeAndRest,
  querySelector,
  setDateRangeFilters,
) {
  const outcome = {stop: false}

  if (!(filterType.match(/^date$/i) && ['', '='].includes(filterOperator))) {
    return outcome
  }

  outcome.stop = true

  let {dateType, dateRest} = getDateTypeAndRest(filterValue)

  if (!dateRest) {
    return outcome
  }

  // get date preposition
  const match = dateRest.match(/^(after|before|between)(.*)$/i)

  const datePreposition = match ? match[1] : null

  // get the rest of the date
  dateRest = match ? match[2].trim() : dateRest

  // break up the rest in to two possible pieces
  let [date1, date2] = dateRest.split(/ (?:and|to) /i)

  date1 = parseDate(date1)
  date2 = parseDate(date2)

  // exclude date2 if not used
  if (datePreposition === 'after') {
    date2 = null

    date1 = startOfDay(date1)
  }

  // copy date1 to date2 if before (cause it is the end)
  if (datePreposition === 'before') {
    date2 = date1
    date1 = null

    date2 = endOfDay(subDays(date2, 1))
  }

  // got nothing
  if (!date1 && !date2) {
    return outcome
  }

  // flip dates to put oldest at the start
  if (date1 && date2 && date1 > date2) {
    ;[date2, date1] = [date1, date2]
  }

  const query = querySelector(getState())

  // update no matter what, maybe nothing changed <shrug>
  outcome.hash = setDateRangeFilters({
    startDate: date1,
    endDate: date2,
    dateFilterType: dateType,
    timezone: query.timezone,
  })

  return outcome
}

export function processIntervalFilterValue(
  inputValue,
  filterType,
  filterOperator,
  filterValue,
  getDateTypeAndRest,
  querySelector,
  setDateRangeFilters,
) {
  const outcome = {stop: false}

  if (
    !(filterType.match(/^interval$/i) && ['', '='].includes(filterOperator))
  ) {
    return outcome
  }

  outcome.stop = true

  // "created 1 hour ago truncated"
  let {dateType, dateRest} = getDateTypeAndRest(filterValue)

  if (!dateRest) {
    return outcome
  }

  // throw out fluff
  dateRest = dateRest.replace(/^interval( of)? /, '')

  // "1 hour ago" or "0.5m ago trunc" or "50 months truncated"
  let [, intervalAmount, intervalType, intervalTrunc] =
    dateRest.match(
      /^(\d+(?:\.\d+)?)[ ]*([a-z]+)[ ]*(?:ago)?[ ]*(trunc(?:ate(?:d)?)?)?$/i,
    ) || []

  intervalAmount = Number(intervalAmount)

  if (!intervalAmount || intervalAmount <= 0) {
    return outcome
  }

  intervalType = REPORT_INTERVAL_ABBV_LOOKUP[intervalType]

  if (!intervalType) {
    return outcome
  }

  intervalTrunc = !!intervalTrunc

  const query = querySelector(getState())

  // update no matter what, maybe nothing changed <shrug>
  outcome.hash = setDateRangeFilters({
    dateFilterType: dateType,
    intervalType,
    intervalAmount,
    intervalTrunc,
    timezone: query.timezone,
  })

  return outcome
}

export default function OmniBar({
  placeholder,
  inputValue,
  value,
  onRemoveValue,
  onClear,
  onInputValueChange,
  onProcessInputValue,
  onEditValue,
}) {
  return (
    <div className="flex flex-grow-2">
      <CreatableSelect
        className="omni-bar-container flex-grow-2"
        classNamePrefix="omni-bar"
        components={{
          DropdownIndicator: null,

          MultiValueContainer: function MultiValueContainer({
            innerProps,
            ...props
          }) {
            return (
              <components.MultiValueContainer
                innerProps={{
                  ...innerProps,
                  className: `omni-bar_value-type_${props.data.type} ${
                    innerProps.className || ''
                  }`,
                  onClick: (event) => {
                    if (
                      !hasClass(
                        event.target,
                        'omni-bar__multi-value__remove',
                        'omni-bar__multi-value',
                      )
                    ) {
                      onEditValue(props.data)
                    }
                  },
                }}
                {...props}
              />
            )
          },
        }}
        styles={{
          multiValue: (styles, {data}) => {
            return {
              ...styles,
              ...(data.color
                ? tagColors(data.color)
                : {backgroundColor: '#eee'}),
            }
          },
        }}
        isMulti
        isClearable
        menuIsOpen={false}
        inputValue={inputValue}
        value={value}
        placeholder={placeholder}
        onChange={(_, {action, removedValue}) => {
          if (action === 'pop-value') {
            removedValue = value[value.length - 1]
            action = 'remove-value'
          }

          if (action === 'remove-value' && removedValue) {
            onRemoveValue(removedValue)
          } else if (action === 'clear') {
            onClear()

            onInputValueChange('')
          }
        }}
        onInputChange={(value, {action}) => {
          if (action !== 'input-blur' && action !== 'menu-close') {
            onInputValueChange(value)
          }
        }}
        onKeyDown={(event) => {
          if (['Enter', 'Tab'].includes(event.key)) {
            event.preventDefault()

            onProcessInputValue(inputValue)
          }
        }}
      />

      <button
        className="btn btn--main-search btn--omni-bar-search"
        type="submit"
        onClick={() => onProcessInputValue(inputValue)}
      >
        <Icon name="search" className="fs-01" />
      </button>
    </div>
  )
}

OmniBar.propTypes = {
  placeholder: PropTypes.string,
  inputValue: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.any,
      type: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
    }),
  ).isRequired,
  onRemoveValue: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onInputValueChange: PropTypes.func.isRequired,
  onProcessInputValue: PropTypes.func.isRequired,
  onEditValue: PropTypes.func.isRequired,
}
